import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Space, Spin, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { getAuditLogs } from "../../services/audit/audit.service";
import { AuditLogEntry } from "../../services/audit/audit.types";
import { resolveAuditActionTag } from "../../constants/status-colors";
import { summarizeChanges, FieldChange } from "./utils";

const { Text } = Typography;

const MAX_VISIBLE_CHANGES = 6;
const PAGE_SIZE = 20;

export interface EntityHistoryProps {
  /** Backend route prefix for this entity: "resources", "courses", etc. */
  entityType: string;
  entityId: number;
}

const ChangeLine: React.FC<{ change: FieldChange }> = ({ change }) => (
  <div>
    <Text strong>{change.field}</Text>:{" "}
    {change.from !== undefined && (
      <>
        <Text code>{change.from}</Text> → {" "}
      </>
    )}
    <Text code>{change.to}</Text>
  </div>
);

const EntityHistory: React.FC<EntityHistoryProps> = ({
  entityType,
  entityId,
}) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAuditLogs({
        entity_type: entityType,
        entity_id: entityId,
        page: 1,
        limit: PAGE_SIZE,
      });
      setLogs(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Error al cargar el historial de este elemento."
        action={
          <Button size="small" onClick={fetchLogs}>
            Reintentar
          </Button>
        }
      />
    );
  }

  if (logs.length === 0) {
    return <Empty description="Sin cambios registrados para este elemento." />;
  }

  return (
    <Timeline
      items={logs.map((entry, index) => {
        // Entries are chronologically descending, so the entry right after
        // this one in the array is the previous state in time.
        const previousEntry = logs[index + 1];
        const changes = summarizeChanges(entry, previousEntry);
        const visibleChanges = changes.slice(0, MAX_VISIBLE_CHANGES);
        const hiddenCount = changes.length - visibleChanges.length;
        const actionTag = resolveAuditActionTag(entry.action);

        return {
          key: entry.id,
          children: (
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Space wrap>
                <Text type="secondary">
                  {dayjs(entry.created_at).format("DD/MM/YYYY HH:mm")}
                </Text>
                <Tag color={actionTag.color}>{actionTag.label}</Tag>
              </Space>
              <Space direction="vertical" size={0}>
                <Text>{entry.actor_name}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {entry.actor_email}
                </Text>
              </Space>
              {entry.action === "DELETE" ? (
                <Text type="secondary">Elemento eliminado.</Text>
              ) : (
                <Space direction="vertical" size={0}>
                  {visibleChanges.map((change) => (
                    <ChangeLine key={change.field} change={change} />
                  ))}
                  {hiddenCount > 0 && (
                    <Text type="secondary">+{hiddenCount} más</Text>
                  )}
                </Space>
              )}
            </Space>
          ),
        };
      })}
    />
  );
};

export default EntityHistory;
